/**
 * Runtime half of the landing-flow router: gathers the inputs for the
 * pure {@link resolveLandingDecision} rule (auth, flags, store, URL) and
 * performs the single side effect of entering the chosen first run.
 * `CampaignLandingGate` is the thin React shell around this.
 */

import { getCurrentUser, isAuthAvailable, waitForAuthReady } from "./auth";
import { consumeHandoffParams } from "./handoff";
import { getFirstTouch, getLastTouch } from "./attribution";
import { COLLAB_ONBOARDING_FLAG, FENG_SHUI_ONBOARDING_FLAG, getFlag, RENDER_3D_ONBOARDING_FLAG } from "./flags";
import { initFlagsClient } from "./flagsClient";
import {
  fengShuiStartPhase,
  hasExcludedDeepLink,
  isCampaignLanding,
  landingUtmContent,
  resolveLandingDecision,
  type LandingFlow,
  type LandingReason,
} from "./landingFlow";
import { isUnstartedRoom, useDesign } from "./store";
import { enterCollabOnboarding } from "./collabOnboarding";
import { enterFengShuiOnboarding } from "./fengShuiOnboarding";
import { enterRender3dOnboarding } from "./render3dOnboarding";
import { loadRender3dEngine } from "./render3d/loadEngine";
import { addBreadcrumb, track } from "./telemetry";

/**
 * Longest a campaign visitor waits for Firebase Auth to restore a session
 * (and Remote Config to answer) before we assume "signed-out" and route.
 * Persisted sessions usually resolve in well under a second; the cap
 * keeps a slow network from turning the ad click into a blank screen.
 */
export const LANDING_GATE_TIMEOUT_MS = 1500;

function prefetchRender3dEngine(): void {
  if (typeof window === "undefined") return;
  const load = () => void loadRender3dEngine().catch(() => {});
  const idle = (window as Window & { requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => number })
    .requestIdleCallback;
  if (idle) idle(load, { timeout: 2000 });
  else setTimeout(load, 600);
}

/** Whether this page load should go through the gate at all. */
export function isCampaignPageLoad(): boolean {
  if (typeof window === "undefined") return false;
  const search = window.location.search;
  if (hasExcludedDeepLink(search)) return false;
  return isCampaignLanding(search, getLastTouch() ?? getFirstTouch());
}

/**
 * Wait (bounded) for auth + flags, evaluate the rule, and enter the
 * chosen first run when it applies. Resolves to the flow that was
 * actually applied, and reports it as `landing_flow_decided`.
 */
export async function decideLanding(
  timeoutMs = LANDING_GATE_TIMEOUT_MS
): Promise<LandingFlow> {
  let timedOut = false;
  const timeout = new Promise<void>((resolve) =>
    setTimeout(() => {
      timedOut = true;
      resolve();
    }, timeoutMs)
  );
  // A room (and first-run state) handed over from an in-app browser lands
  // on the canvas before the rule runs, so the rule sees "room started"
  // and the restored first run keeps its place instead of restarting.
  const handoff = await consumeHandoffParams();
  // `initFlagsClient` is idempotent — `FlagsProvider` calls it again on
  // mount. Awaiting it here lets a Remote Config kill switch apply on the
  // very first visit when the fetch is quick.
  await Promise.race([
    Promise.all([waitForAuthReady(), initFlagsClient()]),
    timeout,
  ]);

  const search = window.location.search;
  const attribution = getLastTouch() ?? getFirstTouch();
  // An anonymous first-run session is not an account: it must still be
  // routed like a new visitor.
  const user = getCurrentUser();
  const authStatus = !isAuthAvailable()
    ? "unavailable"
    : user && !user.isAnonymous
      ? "signed-in"
      : "signed-out";
  const decision = resolveLandingDecision({
    search,
    attribution,
    authStatus,
    roomStarted: !isUnstartedRoom(useDesign.getState().design.room),
    flagEnabled: getFlag(COLLAB_ONBOARDING_FLAG),
    fengShuiFlagEnabled: getFlag(FENG_SHUI_ONBOARDING_FLAG),
    render3dFlagEnabled: getFlag(RENDER_3D_ONBOARDING_FLAG),
  });

  let flow: LandingFlow = decision.flow === "pending" ? "normal" : decision.flow;
  let reason: LandingReason = decision.reason;
  const entered =
    flow === "render_3d"
      ? enterRender3dOnboarding()
      : flow === "feng_shui"
        ? enterFengShuiOnboarding({ phase: fengShuiStartPhase(search) })
        : flow === "collab"
          ? enterCollabOnboarding()
          : true;
  if (!entered) {
    flow = "normal";
    reason = "seed_failed";
  }
  // "Step inside" is the landing's only ask: fetch the 3D engine while the
  // visitor reads the hook, after the first paint has had its turn.
  if (flow === "render_3d") prefetchRender3dEngine();
  track("landing_flow_decided", {
    flow,
    reason,
    utm_content: landingUtmContent(search, attribution),
    timed_out: timedOut,
  });
  addBreadcrumb("landing:decided", {
    flow,
    reason,
    auth: authStatus,
    timed_out: timedOut,
    handoff_room: handoff.room,
  });
  return flow;
}
